import { Box, Flex, IconButton, Text, Tooltip, useColorModeValue, useToast } from '@chakra-ui/react';
import { MouseEvent } from 'react';
import { FaCopy } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { AdminNotificationFeedItemDto, AdminNotificationType } from '../../../models/admin-notification/dto/admin-notification-feed.dto';
import { typography } from 'theme/tokens';
import { translate } from '../../../i18n';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { formatRelativeTime, getTypeColor, getTypeLabel } from '../utils/admin-notification.utils';

function getNotificationUrl(notification: AdminNotificationFeedItemDto): string | null {
  const data = notification.data;
  if (!data) return null;
  if (data.gameId) return `/admin/gameDetails?game=${data.gameId}`;
  return null;
}

interface NotificationFeedItemProps {
  notification: AdminNotificationFeedItemDto;
  onMarkAsRead: (id: string) => void;
  onClose?: () => void;
}

export default function NotificationFeedItem({ notification, onMarkAsRead, onClose }: NotificationFeedItemProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const { selectedOrganization } = useSelectedOrganization();
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const textPrimary = useColorModeValue('secondaryGray.900', 'white');
  const textSecondary = useColorModeValue('secondaryGray.600', 'gray.400');
  const unreadBg = useColorModeValue('brand.50', 'whiteAlpha.50');

  const replacementToken = notification.data?.replacementToken as string | undefined;
  const replacementLink = replacementToken && selectedOrganization?.baseUrl
    ? `${selectedOrganization.baseUrl}/replacement/${replacementToken}`
    : null;

  const handleCopyReplacementLink = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!replacementLink) return;
    navigator.clipboard?.writeText(replacementLink).then(() => {
      toast({
        title: translate('game.replacement_link_copied'),
        status: 'success',
        duration: 2000,
      });
    });
  };

  return (
    <Flex
      align="flex-start"
      px="12px"
      py="10px"
      borderRadius="8px"
      cursor="pointer"
      bg={notification.isRead ? 'transparent' : unreadBg}
      _hover={{ bg: hoverBg }}
      onClick={() => {
        if (!notification.isRead) onMarkAsRead(notification.id);
        const url = getNotificationUrl(notification);
        if (url) {
          navigate(url);
          onClose?.();
        }
      }}
      gap="10px"
    >
      {/* Type indicator dot */}
      <Box
        w="8px"
        h="8px"
        borderRadius="full"
        bg={getTypeColor(notification.type)}
        mt="6px"
        flexShrink={0}
      />

      <Box flex={1} minW={0}>
        <Flex justify="space-between" align="center" mb="2px">
          <Text {...typography.bodyBold} color={textPrimary} isTruncated>
            {notification.title}
          </Text>
          <Text {...typography.caption} color={textSecondary} flexShrink={0} ml="8px">
            {formatRelativeTime(notification.createdAt)}
          </Text>
        </Flex>
        <Text {...typography.caption} color={textSecondary} noOfLines={2}>
          {notification.message}
        </Text>
        <Flex justify="space-between" align="center" mt="2px" gap="6px">
          <Text {...typography.caption} color={getTypeColor(notification.type)}>
            {getTypeLabel(notification.type)}
          </Text>
          {replacementLink && (
            <Tooltip label={replacementLink} hasArrow placement="top" openDelay={300}>
              <IconButton
                aria-label={translate('game.replacement_copy_link')}
                icon={<FaCopy />}
                size="xs"
                variant="ghost"
                colorScheme="blue"
                onClick={handleCopyReplacementLink}
              />
            </Tooltip>
          )}
        </Flex>
      </Box>

      {/* Unread indicator */}
      {!notification.isRead && (
        <Box
          w="6px"
          h="6px"
          borderRadius="full"
          bg="brand.500"
          mt="6px"
          flexShrink={0}
        />
      )}
    </Flex>
  );
}
